import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { required } from "../../../utils/validators";
import { useFormValidation } from "../../../utils/useFormValidation";
import { ModalShell } from "../ModalShell";
import { ModalActions } from "../ModalActions";
import { NAV_ICONS } from "../../../components/icons/navIcons";
import clsx from "clsx";
import type { ClauseType } from "../../../types/clauseType.types";
import { getApiErrorMessage } from "../../../utils/apiError";

interface ClauseTypeModalProps {
  value?: ClauseType;
  onClose: () => void;
  onSave: (data: { id?: string; clauseType: string }) => Promise<void>;
  mode?: "add" | "edit" | "view";
}

export function ClauseTypeModal({
  value,
  onClose,
  onSave,
  mode = "add",
}: ClauseTypeModalProps) {
  const { t } = useTranslation("clauseTypes");
  const isView = mode === "view";
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const refs = {
    clauseType: useRef<HTMLInputElement>(null),
  };

  const [form, setForm] = useState({
    id: value?.id || "",
    clauseType: value?.clauseName || "",
  });

  const { errors, isValid, markAllTouched, shouldShowError, focusFirstError } =
    useFormValidation(form, {
      clauseType: [required],
    });

  const handleSave = async () => {
    if (!isValid) {
      markAllTouched();
      focusFirstError(refs);
      return;
    }

    try {
      setSaving(true);
      setApiError(null);

      await onSave(form);
    } catch (e: any) {
      setApiError(getApiErrorMessage(e, t("clauseType.messages.saveFailedMessage", {
            defaultValue: "Failed to save clause type",
          })));
    } finally {
      setSaving(false);
    }
  };

  const labelClass =
    "block text-sm font-medium mb-1 text-[rgb(var(--text-primary))]";
  const inputClass =
    "input w-full text-[rgb(var(--text-primary))] transition-all";

  return (
    <ModalShell
      title={
        <div className="flex items-center gap-2">
          <NAV_ICONS.organisation size={18} />
          <span className="font-medium">
            {isView
              ? t("clauseType.modalTitle.view", {
                  defaultValue: "View Clause Type",
                })
              : value
                ? t("clauseType.modalTitle.edit", {
                    defaultValue: "Edit Clause Type",
                  })
                : t("clauseType.modalTitle.add", {
                    defaultValue: "Add Clause Type",
                  })}
          </span>
        </div>
      }
      onClose={onClose}
    >
      <div className="flex flex-col gap-5">
        <div className="grid grid-cols-1 gap-5">
          {/* SECTION HEADER */}
          <div>
            <h3 className="font-semibold text-sm border-b pb-1 text-[rgb(var(--text-accent))]">
              {t("clauseType.sectionHeaders.generalInformation", {
                defaultValue: "General Information",
              })}
            </h3>
          </div>

          {/* CLAUSE TYPE INPUT */}
          <div>
            <label className={labelClass}>
              {t("clauseType.labels.type", {
                defaultValue: "Clause Type",
              })}{" "}
              <span className="text-red-600">*</span>
            </label>
            <input
              ref={refs.clauseType}
              type="text"
              disabled={isView}
              placeholder={t("clauseType.placeholders.typeExample", {
                defaultValue: "e.g. Indemnity, Confidentiality",
              })}
              aria-invalid={(shouldShowError("clauseType") && errors.clauseType) ? true : undefined}
              className={clsx(
                inputClass,
                shouldShowError("clauseType") &&
                  errors.clauseType &&
                  "input-invalid",
              )}
              value={form.clauseType}
              onChange={(e) =>
                setForm({ ...form, clauseType: e.target.value })
              }
            />
            {shouldShowError("clauseType") && errors.clauseType && (
              <p className="text-xs text-red-600 mt-1">{errors.clauseType}</p>
            )}
          </div>
        </div>

        {apiError && (
          <div className="p-3 bg-red-50 border border-red-100 rounded-lg text-red-600 text-sm font-medium">
            {apiError}
          </div>
        )}
      </div>

      {!isView && (
        <ModalActions
          onClose={onClose}
          onSave={handleSave}
          disabled={saving}
          loading={saving}
        />
      )}
    </ModalShell>
  );
}
